import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  Loader2, AlertCircle, CheckCircle2, FileCode, Cpu, Database,
  ListChecks, ShieldCheck, ArrowRight,
} from 'lucide-react';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Poll cadence — 3 s feels live without hammering Supabase.
const POLL_INTERVAL_MS = 3000;

// Ordered list of pipeline phases we recognise from the
// ``processing_step`` string. We use the FIRST match (case-insensitive
// substring) to decide which row in the timeline is active.
const PHASES = [
  { key: 'parsing',   label: 'Parsing IFC model',          match: ['parsing ifc', 'parsing model'], icon: FileCode },
  { key: 'rules',     label: 'Loading project rules',      match: ['loading project rules', 'loading rules'], icon: ListChecks },
  { key: 'quality',   label: 'Running Quality Gate',       match: ['quality gate'], icon: Cpu },
  { key: 'aim',       label: 'Building AIM Register',      match: ['aim register'], icon: Database },
  { key: 'om',        label: 'O&M cross-reference',        match: ["o&m", 'om '], icon: ShieldCheck },
  { key: 'cobie',     label: 'COBie AI alignment',         match: ['cobie'], icon: ShieldCheck },
  { key: 'finalize',  label: 'Finalizing report',          match: ['summary', 'complete'], icon: CheckCircle2 },
];

function phaseFromStep(step) {
  if (!step) return 'parsing';
  const s = step.toLowerCase();
  for (const p of PHASES) {
    if (p.match.some((m) => s.includes(m))) return p.key;
  }
  return 'parsing';
}

/**
 * Circular animated progress indicator.
 *
 * Two layers:
 *   • A slow-rotating dashed track (the "liveness" cue — proves to the
 *     user the page isn't frozen even when the percentage hasn't
 *     advanced in a few seconds).
 *   • A foreground arc that fills proportionally to ``percent``.
 *
 * Renders entirely as SVG so it scales crisply and doesn't depend on
 * any JS animation lib — pure CSS keyframes drive the rotation.
 */
function CircularProgress({ percent, statusLabel }) {
  const size = 280;
  const stroke = 16;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safePct = Math.max(0, Math.min(100, percent || 0));
  const offset = circumference * (1 - safePct / 100);
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      {/* Rotating dashed track — liveness cue */}
      <svg className="absolute inset-0 animate-aha-rotate" width={size} height={size}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke="hsl(var(--accent) / 0.25)"
          strokeWidth={stroke / 3}
          strokeDasharray="4 14"
          strokeLinecap="round"
        />
      </svg>
      {/* Static background ring */}
      <svg width={size} height={size} className="absolute inset-0">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={stroke}
        />
      </svg>
      {/* Progress arc */}
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke="hsl(var(--accent))"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
        />
      </svg>
      {/* Center text */}
      <div className="relative z-10 text-center select-none">
        <div className="text-5xl font-bold text-foreground tracking-tight" data-testid="progress-percent">
          {safePct.toFixed(1)}<span className="text-2xl text-muted-foreground">%</span>
        </div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">
          {statusLabel}
        </div>
      </div>
    </div>
  );
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function AuditProgressPage() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectScope = searchParams.get('project') || '';
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  // Used to estimate ETA: we remember the (timestamp, processedCount)
  // pair from the FIRST poll where processedCount > 0, then divide
  // future deltas to project a finish time.
  const startRef = useRef(null);
  // Counts consecutive failed status polls; reset on every success.
  const consecutiveFailsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const poll = async () => {
      try {
        const url = projectScope
          ? `${API_URL}/api/audits/${auditId}/status?project_id=${encodeURIComponent(projectScope)}`
          : `${API_URL}/api/audits/${auditId}/status`;
        const resp = await apiFetch(url);
        if (!resp.ok) {
          throw new Error(`Status check failed (${resp.status})`);
        }
        const data = await resp.json();
        if (cancelled) return;
        setStatus(data);
        // A successful poll clears any transient poll error so one
        // network blip / 500 doesn't leave the page stuck on
        // "STOPPED" while the audit keeps running.
        consecutiveFailsRef.current = 0;
        setError('');
        // Anchor the rate estimator on the first sample where we have
        // forward motion.
        if (
          !startRef.current
          && (data.processed_element_count || 0) > 0
          && (data.total_elements || 0) > 0
        ) {
          startRef.current = {
            ts: Date.now(),
            processed: data.processed_element_count,
          };
        }
        if (data.status === 'completed' || data.status === 'completed_with_warnings') {
          // Audit finished — bounce to the results page.
          navigate(`/results/${auditId}`, { replace: true });
          return;
        }
        if (data.status === 'failed') {
          // Stay on this page so the user can read the error and decide.
          setError(data.error_message || 'Audit failed.');
          return;
        }
      } catch (err) {
        // Tolerate isolated failures (transient Supabase disconnects
        // surface as 500s). Only show the error banner after 3
        // consecutive failed polls.
        if (!cancelled) {
          consecutiveFailsRef.current += 1;
          if (consecutiveFailsRef.current >= 3) {
            setError(err.message || 'Could not reach the audit service.');
          }
        }
      }
      if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [auditId, projectScope, navigate]);

  // ── Derived display values ──────────────────────────────────────
  const total = status?.total_elements || 0;
  const processed = status?.processed_element_count || 0;
  const percent = total > 0 ? (processed / total) * 100 : 0;
  const currentPhase = phaseFromStep(status?.processing_step);
  // Rate + ETA estimate (only meaningful once we've seen forward motion).
  let etaText = 'Estimating…';
  let elapsedText = '—';
  if (startRef.current && total > 0) {
    const elapsed = Date.now() - startRef.current.ts;
    const done = processed - startRef.current.processed;
    elapsedText = formatDuration(elapsed);
    if (done > 0 && elapsed > 0) {
      const ratePerMs = done / elapsed;
      const remaining = total - processed;
      etaText = formatDuration(remaining / ratePerMs);
    }
  }

  const isFailed = status?.status === 'failed' || !!error;

  return (
    <DashboardLayout>
      <style>{`
        @keyframes aha-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .animate-aha-rotate {
          animation: aha-rotate 14s linear infinite;
          transform-origin: center;
        }
        @keyframes aha-pulse-soft {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
        .animate-aha-pulse {
          animation: aha-pulse-soft 2.4s ease-in-out infinite;
        }
      `}</style>
      <div className="max-w-4xl mx-auto pt-4 pb-12">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground" data-testid="progress-title">
            AHA Quality Gate in progress
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Your audit is running. This page polls every few seconds and will
            forward you to the results the moment it&apos;s done — feel free
            to leave it open in a tab.
          </p>
        </div>

        {isFailed && (
          <Alert variant="destructive" className="mb-6" data-testid="progress-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="whitespace-pre-line">
              {error || status?.error_message || 'Audit failed.'}
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-accent/40 overflow-hidden">
          <CardContent className="pt-10 pb-10">
            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-10 items-center">
              {/* Left — circular gauge */}
              <div className="flex justify-center">
                <CircularProgress
                  percent={percent}
                  statusLabel={isFailed ? 'Stopped' : 'Validating'}
                />
              </div>

              {/* Right — status + ETA + IFC name */}
              <div className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">
                    Current step
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-base font-medium text-foreground"
                       data-testid="progress-current-step">
                    {!isFailed && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
                    <span>{status?.processing_step || 'Starting up…'}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Elements
                    </div>
                    <div className="text-xl font-semibold text-foreground" data-testid="progress-elements">
                      {processed.toLocaleString()}<span className="text-muted-foreground"> / {total.toLocaleString()}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Time elapsed
                    </div>
                    <div className="text-xl font-semibold text-foreground" data-testid="progress-elapsed">
                      {elapsedText}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Estimated time remaining
                    </div>
                    <div className="text-xl font-semibold text-foreground" data-testid="progress-eta">
                      {isFailed ? '—' : etaText}
                    </div>
                  </div>
                </div>

                {status?.ifc_file_hash && (
                  <div className="pt-2 border-t border-border">
                    <div className="text-xs text-muted-foreground">
                      File hash: <span className="font-mono">{status.ifc_file_hash.slice(0, 14)}…</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline of pipeline phases */}
        <Card className="mt-6">
          <CardContent className="py-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
              Pipeline
            </div>
            <ul className="space-y-3" data-testid="progress-timeline">
              {PHASES.map((p, idx) => {
                const isActive = currentPhase === p.key;
                const activeIdx = PHASES.findIndex((q) => q.key === currentPhase);
                const isDone = activeIdx > -1 && idx < activeIdx;
                const Icon = p.icon;
                return (
                  <li
                    key={p.key}
                    className={`flex items-center gap-3 text-sm ${
                      isActive ? 'text-foreground' : isDone ? 'text-muted-foreground' : 'text-muted-foreground/60'
                    }`}
                    data-testid={`progress-phase-${p.key}`}
                  >
                    <div
                      className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isActive
                          ? 'bg-accent text-accent-foreground animate-aha-pulse'
                          : isDone
                          ? 'bg-[hsl(var(--success)_/_0.15)] text-[hsl(var(--success))]'
                          : 'bg-muted text-muted-foreground/70'
                      }`}
                    >
                      {isDone ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
                    </div>
                    <span>{p.label}</span>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(projectScope ? `/projects/${projectScope}` : '/projects')}
            data-testid="progress-back-button"
          >
            ← Back to project
          </Button>
          {(status?.status === 'completed' || status?.status === 'completed_with_warnings') && (
            <Button
              size="sm"
              onClick={() => navigate(`/results/${auditId}`)}
              data-testid="progress-view-results"
            >
              View results <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
